// Created: 2026-08-14
// Updated: 2026-08-14
//
// USER RELATIONS query hook (react-query-user-relations, Req 2).
//
// One hook, one key, one endpoint. Thin layer, same shape as slices 1–6: the transport stays in
// `src/api/secure/social.ts` and is not duplicated here, and the ONLY caller is
// `src/context/UserRelationsContext.tsx`, which turns this entry into the six derived context members.
// The writes live in `mutations.ts`.
//
// ─── THE GATE IS COMPUTED BY THE CALLER, NOT BY THE HOOK ──────────────────────────────────────────
//
// Unlike slice 6's hooks — which call `useAuth()` themselves and hard-code `enabled: !!idToken` — this
// hook takes both `idToken` and a fully resolved `enabled` boolean as arguments. Two reasons, and both are
// specific to this slice:
//
//   1. **The gate has a THIRD conjunct that only the provider can see.** `enabled` is
//      `isAuthenticated && !!idToken && pathname !== '/create/profile'` (Req 2.3). That pathname term is
//      the pre-migration `window.location.pathname === '/create/profile'` early return, and it has to be
//      read through `useLocation()` to be REACTIVE — `window.location` is invisible to React and was
//      re-read only when the old effect happened to run. The provider is the component that sits under
//      `BrowserRouter` and owns both `useAuth()` and `useLocation()`, so the resolved boolean is computed
//      where those inputs live and passed down.
//   2. **It keeps this module a pure query wrapper.** A test can render this hook with nothing but a
//      `QueryClientProvider` — no `AuthProvider`, no router — and drive `enabled` directly as an input.
//      The route- and auth-dependent behavior is then asserted once, against the provider, where it
//      actually lives.
//
// An absent `idToken` means the query is **DISABLED**, never "call unauthenticated" (Req 2.1). The
// `idToken as string` cast inside `queryFn` is sound for exactly that reason: React Query does not invoke
// `queryFn` while `enabled` is false, and every caller's `enabled` carries `!!idToken` as a conjunct.
//
// ─── NO `staleTime` / `gcTime` OVERRIDE — THE BASELINE APPLIES ────────────────────────────────────
//
// `src/queries/queryClient.ts` sets 5 min / 10 min client-wide and this query overrides neither
// (Req 2.8). Slices 4 and 6 each set a 30 s override, and the reason they did is the reason this one must
// not: their data is moved by OTHER actors (someone follows you, a moderator acts, a notification
// arrives), so shortening the window is the only way to converge. This payload has no such actor.
//
//   * **Favorites and follows are moved by THIS client.** Every transition is a mutation this browser
//     issued, and `mutations.ts` writes the new truth into this entry itself — optimistically at the click
//     for the favorite, on resolution for the follow. There is nothing for a shorter window to discover,
//     which is also why neither mutation invalidates: a refetch would replace a correct entry with an
//     identical one.
//   * **`isModerator` cannot change inside a session.** The handler derives it from the request's
//     `cognito:groups` claim, which is baked into the token at issuance. Granting or revoking moderator
//     status takes effect only when a new token is minted — at which point `idToken` changes, `enabled`
//     re-evaluates, and (because sign-out purges this key, design.md § 3) the next session fetches fresh.
//     Polling a claim on a timer cannot make the claim change sooner.
//   * **`unpublishedProjects` is absent from the response entirely** (BL-001, see `types.ts`). A shorter
//     window would refetch a field the server does not send.
//
// The comparison that settles it: pre-migration this data was fetched ONCE per session and then pinned by
// a `lastFetchedIdToken` ref — an effective `staleTime` of `Infinity`. The 5-minute baseline is already
// strictly fresher than the behavior being replaced, so an override would not fix a staleness bug (there
// is none here); it would add N requests per session to converge data this client already wrote or the
// token already fixed.

import { useQuery, type UseQueryResult } from '@tanstack/react-query'

import { fetchUserRelations } from '../../api/secure/social'

import { userRelationsKeys } from './keys'
import type { WebsiteUserRelations } from './types'

export type UseUserRelationsQueryArgs = {
  /** Cognito id token. Absent means the query is DISABLED, never "call unauthenticated" (Req 2.1). */
  idToken: string | null | undefined
  /**
   * The resolved gate: `isAuthenticated && !!idToken && pathname !== '/create/profile'` (Req 2.3).
   * Computed by the caller — see the module header for why it is not computed here.
   */
  enabled: boolean
}

/**
 * PERSONAL — the caller's relations payload. Key `['me','relations']`.
 *
 * `queryFn` forwards React Query's `signal` into the read (Req 2.7). That forward is load-bearing rather
 * than decorative: `secureFetch` (`src/api/secure/helpers.ts:81-92`) destructures `signal` and threads it
 * into BOTH the initial request and its 401 refresh-retry, so a relations read abandoned by a sign-out or
 * a navigation becomes a CANCELLATION at both hops instead of a query error.
 *
 * `enabled` is passed straight through, untouched. The hook adds no conjunct of its own — if it did, the
 * provider's `enabled ? data : undefined` derive-guard and this hook's gate could disagree about whether
 * the query is live, and the whole point of D1 is that those two agree.
 *
 * The response is cached VERBATIM (Req 1.8): no normalisation, no `Set` construction, no default-filling
 * on the way in. Every coercion happens at derivation time in the provider, so there is exactly one copy
 * of the truth and it cannot disagree with itself between two consumers.
 */
export function useUserRelationsQuery({
  idToken,
  enabled,
}: UseUserRelationsQueryArgs): UseQueryResult<WebsiteUserRelations, Error> {
  return useQuery<WebsiteUserRelations, Error>({
    queryKey: userRelationsKeys.all(),
    // `signal` forward — the abort-as-cancellation seam, threaded into the 401 retry too. Keep it.
    queryFn: ({ signal }) => fetchUserRelations(idToken as string, { signal }),
    enabled,
  })
}
